import { useCallback } from 'react';

import type { ApolloLink } from '@apollo/client';
import { gql } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import { uniqWith } from 'ramda';
import { useCurrentUserId } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { userTypename } from 'src/helpers';

import {
  activeReportVar,
  markReportGridForRefresh,
  refreshDataGrid,
  updatedCachedReportRows,
} from './reports';

export function useGetFile(fileId: UUID | null): {
  file: NonNullable<Apollo.GetFileQueryResult['data']>['file'] | null;
  loading: boolean;
} {
  const { data, loading } = Apollo.useGetFileQuery({
    skip: !fileId,
    variables: { fileId: fileId ?? '' },
  });

  return {
    file: data?.file ?? null,
    loading,
  };
}

type CreateFilesOptions = Parameters<Apollo.CreateFilesMutationHookResult[0]>[0] & {
  reportId?: UUID;
};

export function useCreateFiles(): {
  createFiles: (
    options: CreateFilesOptions
  ) => Promise<ApolloLink.Result<Apollo.CreateFilesMutation>>;
} {
  const currentUserId = useCurrentUserId();
  const [createFiles] = Apollo.useCreateFilesMutation();
  const safeCreateFiles = useCallback(
    ({ reportId, ...apolloOpts }: CreateFilesOptions) => {
      return createFiles({
        ...apolloOpts,
        update(cache, { data }) {
          const newFileRefs =
            data?.createFiles.flatMap(file => {
              const ref = cache.writeFragment({
                data: file,
                fragment: gql`
                  fragment CreateFiles on Document {
                    __typename
                    id
                  }
                `,
              });
              return ref ? [ref] : [];
            }) ?? [];

          // Add files to my recent documents.
          if (currentUserId) {
            cache.modify({
              fields: {
                myRecentDocuments: (myRecentDocumentsRefs, { readField }) => {
                  if (!myRecentDocumentsRefs.length) return newFileRefs;

                  return uniqWith(
                    (a, b) => readField<UUID>('id', a) === readField<UUID>('id', b),
                    [...newFileRefs, ...myRecentDocumentsRefs]
                  );
                },
              },
              id: cache.identify({ __typename: userTypename, id: currentUserId }),
            });
          }

          if (reportId) {
            updatedCachedReportRows({
              action: {
                docRefs: newFileRefs,
                type: 'add',
              },
              cache,
              reportId,
            });

            /**
             * If the matching grid is not active (e.g. the Documents pane is
             * hidden in React.Activity while an upload finishes), defer the
             * refresh until that grid re-activates so the list isn't stale.
             */
            const activeReport = activeReportVar();
            if (activeReport?.reportId === reportId) refreshDataGrid(activeReport.apiRef);
            else markReportGridForRefresh(reportId);
          }
        },
      });
    },
    [createFiles, currentUserId]
  );

  return {
    createFiles: safeCreateFiles,
  };
}

export type DocumentAsset = NonNullable<
  Apollo.GetDocumentPartsQueryHookResult['data']
>['documentParts']['assets'][number];

export function useDocumentParts(): {
  getDocumentParts: (
    documentId: string
  ) => Promise<NonNullable<Apollo.GetDocumentPartsQueryHookResult['data']>['documentParts']>;
} {
  const client = useApolloClient();
  const getDocumentParts = useCallback(
    async (documentId: string) => {
      const { data } = await client.query<
        Apollo.GetDocumentPartsQuery,
        Apollo.GetDocumentPartsQueryVariables
      >({
        fetchPolicy: 'network-only',
        query: Apollo.GetDocumentPartsDocument,
        variables: {
          documentId,
        },
      });
      return (
        data?.documentParts ?? {
          __typename: 'DocumentParts' as const,
          assets: [],
          authors: [],
          comments: [],
        }
      );
    },
    [client]
  );

  return { getDocumentParts };
}
